/**
 * Wire protocol. Imported by the client and by both services, so that a message
 * shape can only be wrong in one place.
 *
 * Invariant: a field key is `{entity}:{recordUuid}:{fieldName}` and is
 * view-independent. See ADR-0006.
 */

export type Uuid = string;          // UUIDv7, lowercase, hyphenated on the wire
export type FieldKeyString = string; // "report:018f...:title"

export interface Hlc {
  wall: number;      // ms since epoch, ordering only
  counter: number;
  node: Uuid;
}

export type WriteOrigin = 'online' | 'offline' | 'external' | 'import';

export type FieldValue =
  | { t: 'string'; v: string }
  | { t: 'text'; v: string }
  | { t: 'int'; v: number }
  | { t: 'decimal'; v: string }   // string to avoid float drift in money
  | { t: 'bool'; v: boolean }
  | { t: 'date'; v: string }      // yyyy-mm-dd
  | { t: 'datetime'; v: string }  // ISO 8601
  | { t: 'uuid'; v: Uuid }
  /**
   * Structured data stored on the record itself — a transaction's legs
   * (ADR-0026). A field value like any other: it is leased, versioned and
   * recorded in history as one thing, because that is what it is.
   */
  | { t: 'json'; v: unknown }
  | { t: 'null'; v: null }
  /**
   * One save of a document-mode record, as a single history entry.
   *
   * `value` has always been a tagged union, so a document commit extends the
   * tag set rather than needing a column or a table of its own.
   *
   * `v` is the commit's **change set** — the parent fields that moved and,
   * per child, its `op` and the fields that moved — which is the same
   * information the read path reconstructs today by grouping field rows by
   * HLC, recorded directly instead of inferred.
   *
   * Deliberately NOT a full snapshot yet. A snapshot would make reverting a
   * matter of writing it back rather than replaying, which is the prize; it
   * also costs reading the whole record and its children back on every save.
   * That is a separate step, and the tag does not change when it is taken.
   * See `DOCUMENT_COMMIT_FIELD`.
   */
  | { t: 'document'; v: unknown };

export interface FieldVersion {
  id: Uuid;
  key: FieldKeyString;
  version: number;
  baseVersion: number;
  value: FieldValue;
  author: Uuid | null;          // null for external and import writes
  source: WriteOrigin;
  wallTime: string;             // display only
  clock: Hlc;
}

/** True when the author was not looking at the immediately preceding version. */
export function hasStaleBase(v: FieldVersion): boolean {
  return v.version > 1 && v.baseVersion !== v.version - 1;
}

/**
 * Whether a write would change anything.
 *
 * Here rather than in either side of the wire, because both ask it about the
 * same write: the client, to not send one that says nothing, and the server,
 * because invariant #7 keeps `field_history` for *changes* and a row saying
 * "Renovation became Renovation" is not one. Opening a form, which focuses
 * its first field, and closing it again had been writing one of those every
 * time.
 *
 * Only the scalar tags answer. A `json` or `document` value is structured,
 * two encodings of the same content compare unequal, and the cost of being
 * wrong runs one way: a no-op recorded as a change is noise, a change
 * mistaken for a no-op is data silently not written. So the structured tags
 * return false and are always written.
 */
export function sameFieldValue(a: FieldValue | undefined, b: FieldValue | undefined): boolean {
  if (a === undefined || b === undefined) return false;
  if (a.t !== b.t) return false;
  if (a.t === 'json' || a.t === 'document' || b.t === 'json' || b.t === 'document') return false;
  return a.v === b.v;
}

// ---------------------------------------------------------------- client -> server

export type ClientMessage =
  | { t: 'hello'; schemaVersion: number; node: Uuid }
  | { t: 'subscribe'; entity: string; recordIds: Uuid[] }
  | { t: 'unsubscribe'; entity: string; recordIds: Uuid[] }
  | { t: 'openRecord'; entity: string; recordId: Uuid }
  | { t: 'closeRecord'; entity: string; recordId: Uuid }
  /**
   * Take a free key — or, with `resume`, re-attach the hold this tab already
   * has: the token it was given, kept in the tab's `sessionStorage`. A hold
   * outlives a dropped connection, a sleeping laptop and a reloaded-after-
   * discard tab (ADR-0003, 2026-10-22); `resume` is how the same tab picks it
   * up again. A token that no longer holds it — somebody took it over — is
   * refused like any claim.
   */
  | { t: 'claim'; key: FieldKeyString; resume?: Uuid }
  /**
   * Take a key that somebody else is holding, right now.
   *
   * Deliberately a separate message from `claim` rather than a flag on it.
   * A claim asks "is this free?" and takes no for an answer; a steal asserts
   * "I am taking it", and the server must never turn one into the other by
   * accident. Separating them also means the audit trail and the rights
   * check have somewhere obvious to live, and a client that has not been
   * updated cannot steal at all.
   *
   * Answered with `granted` like a claim. Everyone watching the record gets
   * a `leaseTaken` carrying `stolenFrom`, and the previous holder's fencing
   * token is dead from that moment (the lease id changes), so their in-flight
   * save fails the token check exactly as invariant 2 requires. See ADR-0003.
   */
  | { t: 'steal'; key: FieldKeyString }
  /** Ignored since holds stopped expiring (2026-10-22). Accepted so a tab still on an older page is not an error. */
  | { t: 'renew'; key: FieldKeyString; leaseId: Uuid }
  | { t: 'release'; key: FieldKeyString; leaseId: Uuid; commit?: FieldValue }
  /** Debounced intermediate value for viewers. Never persisted. */
  | { t: 'preview'; key: FieldKeyString; value: FieldValue }
  /** leaseId is absent for `cas` fields such as booleans. See ADR-0007. */
  | { t: 'write'; key: FieldKeyString; value: FieldValue; base: number; clock: Hlc; leaseId?: Uuid }
  /** Ignored since presence moved into `sync`'s memory (2026-10-22); accepted for older pages. */
  | { t: 'heartbeat' };

// ---------------------------------------------------------------- server -> client

export interface PresenceUser {
  id: Uuid;
  name: string;
  initials: string;
  colorHex: string;
}

export type ServerMessage =
  | { t: 'welcome'; me: PresenceUser; serverTime: string }
  | { t: 'presence'; entity: string; recordId: Uuid; users: PresenceUser[] }
  /**
   * "This key is held by `holder`, since `since`, and they are / are not
   * connected." Broadcast to the record's watchers when a hold is taken, taken
   * over, resumed, or its holder's connection comes or goes — a hold no longer
   * lapses (ADR-0003, 2026-10-22), so `online` is the only thing that changes
   * while it stands: "editing now" against "unfinished since …".
   */
  | {
      t: 'leaseTaken'; key: FieldKeyString; holder: Uuid; since: string; online: boolean;
      /**
       * Set when this was a take-over, naming who lost the hold. Also sent to
       * a tab whose `resume` found its hold taken while it was away. Whoever
       * sees their own id here was interrupted deliberately, and keeps their
       * draft and says so.
       */
      stolenFrom?: Uuid;
    }
  /**
   * "Nobody holds this key any more": its holder saved, cancelled, closed the
   * form or left the page. Since holds stopped lapsing (2026-10-22) this is
   * the only way a hold ends besides a take-over, so it always means the
   * record really is free. A holder who merely went offline is a
   * `leaseTaken` with `online: false`, not this.
   */
  | { t: 'leaseFreed'; key: FieldKeyString; reason?: 'released' }
  /** `resumed`: this tab's own hold, picked up again with the token it kept. */
  | { t: 'granted'; key: FieldKeyString; leaseId: Uuid; since: string; resumed?: boolean }
  /** `since`/`online` describe the holder when the reason is `held`, so a refusal can say "editing now" or "unfinished since …". */
  | { t: 'denied'; key: FieldKeyString; holder: Uuid; reason: DenyReason; since?: string; online?: boolean }
  | { t: 'preview'; key: FieldKeyString; value: FieldValue; by: Uuid }
  | { t: 'committed'; key: FieldKeyString; version: FieldVersion }
  | { t: 'rejected'; key: FieldKeyString; reason: RejectReason; current?: FieldVersion }
  | { t: 'recordEvent'; entity: string; recordId: Uuid; kind: RecordEventKind; by: Uuid | null }
  /** The client is too old to write safely. It must reload. See ADR-0016. */
  | { t: 'schemaStale'; required: number }
  | { t: 'error'; message: string };

export type DenyReason = 'held' | 'no-right' | 'immutable';
export type RejectReason =
  | 'stale-base'
  | 'no-lease'
  | 'bad-lease'
  | 'no-right'
  | 'immutable'
  | 'validation'
  /** Reserved: see ADR-0013. Not implemented while webledger has no closing. */
  | 'period-closed';

/**
 * `updated` was added 2026-09-09 alongside ADR-0003's read-only-live-view
 * fix: document-mode commits over REST (`commitDocumentGroup`) previously
 * only ever published `created`, so an edit to an existing document-mode
 * record (e.g. a webledger transaction) never told anyone else it happened —
 * including a read-only viewer whose whole point is to show it live.
 */
export type RecordEventKind = 'created' | 'updated' | 'deleted' | 'restored';

// ---------------------------------------------------------------- key helpers

export function fieldKey(entity: string, recordId: Uuid, field: string): FieldKeyString {
  return `${entity}:${recordId}:${field}`;
}

export function parseFieldKey(key: FieldKeyString): { entity: string; recordId: Uuid; field: string } {
  const parts = key.split(':');
  if (parts.length !== 3) throw new Error(`malformed field key: ${key}`);
  const [entity, recordId, field] = parts as [string, string, string];
  return { entity, recordId, field };
}

/**
 * Document-mode entities (ADR-0010) lease the whole record, not a field —
 * every field on such an entity is typically `lease: 'none'`, so there is no
 * real field left to claim a lease on. This reserved sentinel field name
 * stands in for "the whole record" wherever a `FieldKeyString` is expected
 * (claim/renew/release). `defineApp` rejects a manifest that declares a real
 * field literally named this.
 */
export const RECORD_LEASE_FIELD = '$record';

/**
 * The pseudo-field a document commit is filed under in `field_history`.
 *
 * One table holds both granularities, discriminated by this. That is not
 * thrift: `uq_field_version (entity, record_id, field, version)` then reads,
 * for these rows, as "one commit per version of this record" — a constraint
 * worth having and one a separate table would only get by remembering to add
 * it. A nullable `field` would have been worse than useless, since MySQL
 * counts NULLs as distinct in a unique index and every document row would
 * have slipped past the key unprotected.
 *
 * `$`-prefixed, like `RECORD_LEASE_FIELD`, and the whole prefix is refused to
 * manifests (see `defineApp`), so a real field can never collide with one of
 * these by construction rather than by convention.
 */
export const DOCUMENT_COMMIT_FIELD = '$commit';

/** Pseudo-fields are `$`-prefixed; no manifest may define one. */
export function isReservedFieldName(name: string): boolean {
  return name.startsWith('$');
}

export function recordLeaseKey(entity: string, recordId: Uuid): FieldKeyString {
  return fieldKey(entity, recordId, RECORD_LEASE_FIELD);
}

export { HlcGenerator, compareHlc } from './hlc.js';

// ---------------------------------------------------------------- money

/**
 * Exact decimal arithmetic on scaled `BigInt`, never floats.
 *
 * Money is stored `DECIMAL(18,4)` and arrives as text ("229815.0000") for
 * exactly this reason (`decimalNumbers: false` on the pool). A report that
 * accumulates a running balance over a few hundred legs in JavaScript numbers
 * drifts — and drifts most on the long chronological accumulations, which is
 * precisely where the number is shown to a person who will compare it against
 * their bank. The legacy reports summed with `Number` and papered over the
 * result with an `ABS(...) > 0.0001` epsilon; that epsilon is not needed here
 * and is not reproduced.
 *
 * `scale` is the stored scale (4), not the currency's display fraction. The
 * two are unrelated: a JPY balance is still accumulated at 4dp and only
 * rounded when it is formatted.
 */
export function toScaled(raw: string | number, scale = 4): bigint {
  const text = String(raw).trim();
  const negative = text.startsWith('-');
  const [whole = '0', fraction = ''] = (negative ? text.slice(1) : text).split('.');
  // Truncate rather than round a value longer than the stored scale: the
  // source is already stored at this scale, so anything longer is a caller
  // bug, and silently rounding it would hide that.
  const padded = fraction.slice(0, scale).padEnd(scale, '0');
  const value = BigInt(whole || '0') * 10n ** BigInt(scale) + BigInt(padded || '0');
  return negative ? -value : value;
}

/** Back to the decimal text the rest of the stack passes around. */
export function fromScaled(value: bigint, scale = 4): string {
  const unit = 10n ** BigInt(scale);
  const negative = value < 0n;
  const magnitude = negative ? -value : value;
  const whole = magnitude / unit;
  const fraction = (magnitude % unit).toString().padStart(scale, '0');
  return `${negative ? '-' : ''}${whole}${scale > 0 ? `.${fraction}` : ''}`;
}
